/**
 * Choisir un chapitre dans les programmes de révision d'un élève (`<TutorRevisionProgramSheet>`) :
 * la boîte « Choisir un chapitre » de « Préparer mon live » et la feuille du mode programme de
 * « Recommencer ». Les programmes se lisent à l'ouverture (`getStudyProgramsAsTutor`, la réponse de
 * CET élève seulement) ; le programme montré est celui que le masteur a touché, sinon celui du
 * chapitre déjà retenu (`currentChapterId`), sinon le premier. Une lecture refusée se dit dans la
 * feuille (`error`, « Réessayer ») : la boîte réseau de l'ancien kit se tait sous l'espace tuteur.
 *
 * Mes élèves la lit aussi : la feuille « Programme de révision » de la fiche (qui y ajoute la progression
 * de l'élève, par `adapt`) et le mode programme de « Générer une activité » (qui lit le chapitre choisi
 * dans la réponse, `raw`).
 */
import { useCallback, useMemo, useRef, useState } from "react"
import { useSelector } from "react-redux"

import { studyProgramsService } from "../../../../../../data/services/studyProgramsService"
import { type ApiSchoolSubject } from "../../../../adapters/parent/shared/schoolSubjectAdapter"
import {
    initialProgramId,
    toUITutorStudyPrograms,
    type ApiTutorStudyProgram,
} from "../../../../adapters/tutor/Lives/tutorPrepareLiveAdapter"
import { type UITutorStudyProgram } from "../../../types/tutorEleves"
import { apiErrorMessage } from "../../../hooks/apiErrorMessage"
import { useApiFetch } from "../../../hooks/useApiFetch"

/** Ce que dit une lecture des programmes refusée sans message du serveur : en pratique, le réseau. */
export const TUTOR_PROGRAMS_READ_FAILED = "Le programme n'a pas pu se charger. Vérifie ta connexion puis réessaie."

type ProgramId = UITutorStudyProgram["id"]

/** La réponse servie, en programmes à montrer. Stable d'un rendu à l'autre (un `useCallback`). */
export type ProgramsAdapter<Raw extends ApiTutorStudyProgram = ApiTutorStudyProgram> = (
    raw: Raw[] | null,
    schoolSubjects: ApiSchoolSubject[] | null
) => UITutorStudyProgram[]

export function useTutorProgramPicker<Raw extends ApiTutorStudyProgram = ApiTutorStudyProgram>(
    currentChapterId: string | number | null | undefined,
    adapt: ProgramsAdapter<Raw> = toUITutorStudyPrograms
) {
    const schoolSubjects = useSelector(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (state: any) => (state?.data?.staticData?.school_subjects as ApiSchoolSubject[] | undefined) ?? null
    )
    const read = useApiFetch<Raw[]>({
        service: studyProgramsService.getStudyProgramsAsTutor,
        fetchOnMount: false,
        scopeUrlParams: ["student_ids"],
    })
    const programs = useMemo(() => adapt(read.raw, schoolSubjects), [adapt, read.raw, schoolSubjects])
    const [touchedProgramId, setTouchedProgramId] = useState<ProgramId | undefined>(undefined)
    const studentIdRef = useRef<string | number | null>(null)

    const { fetchWith, cancel } = read
    /** Ouvre le choix pour un élève : ses programmes se lisent, rien n'est encore touché. */
    const load = useCallback(
        (forStudent: string | number) => {
            setTouchedProgramId(undefined)
            studentIdRef.current = forStudent
            fetchWith({ student_ids: forStudent })
        },
        [fetchWith]
    )

    /** « Réessayer » de la feuille : relit les programmes de l'élève ouvert. */
    const onRetry = useCallback(() => {
        if (studentIdRef.current != null) fetchWith({ student_ids: studentIdRef.current })
    }, [fetchWith])

    return {
        /** La réponse telle que servie : le cadrage d'un chapitre choisi s'y lit. */
        raw: read.raw,
        programs,
        selectedProgramId: touchedProgramId ?? initialProgramId(programs, currentChapterId),
        onSelectProgram: setTouchedProgramId,
        loading: read.loading,
        error: read.error != null ? apiErrorMessage(read.error, TUTOR_PROGRAMS_READ_FAILED) : null,
        onRetry,
        load,
        /** La boîte se ferme : la lecture en route n'est plus lue. */
        cancel,
    }
}
